import { Aperture, Download, Menu, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { navigate } from '../App';
import { usePhoto } from '../context/PhotoContext';
import { useLanguage } from '../i18n';

const links = ['/support', '/about'] as const;

export function Nav({ path }: { path: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const { darkMode, toggleDarkMode, activePhoto, downloadActive } = usePhoto();
  const { locale, setLocale, t, localeOptions } = useLanguage();

  return (
    <header className="sticky top-0 z-40 border-b border-[color:var(--line)] bg-[color:var(--surface)]/95 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6">
        <button className="flex items-center gap-2 text-sm font-semibold" onClick={() => navigate('/')} aria-label={t('editor.backUpload')}>
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[color:var(--text)] text-[color:var(--surface)]">
            <Aperture size={18} />
          </span>
          <span>LumaFrame</span>
        </button>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((href) => (
            <button
              key={href}
              onClick={() => navigate(href)}
              className={`rounded-md px-3 py-2 text-sm font-medium transition hover:bg-[color:var(--panel-strong)] ${
                path === href ? 'text-[color:var(--text)]' : 'text-[color:var(--muted)]'
              }`}
            >
              {t(href === '/support' ? 'nav.support' : 'nav.about')}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {path === '/editor' && activePhoto ? (
            <button className="primary-button" onClick={() => void downloadActive()}>
              <Download size={16} />
              {t('nav.export')}
            </button>
          ) : null}
          <label className="sr-only" htmlFor="locale-select">
            {t('nav.language')}
          </label>
          <select
            id="locale-select"
            className="hidden h-10 rounded-md border border-[color:var(--line)] bg-[color:var(--panel)] px-2 text-sm font-medium text-[color:var(--text)] md:block"
            value={locale}
            onChange={(event) => setLocale(event.target.value)}
          >
            {localeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="icon-button" onClick={toggleDarkMode} aria-label={darkMode ? t('nav.light') : t('nav.dark')}>
            {darkMode ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button className="icon-button md:hidden" onClick={() => setOpen((value) => !value)} aria-label={t('nav.menu')}>
            <Menu size={18} />
          </button>
        </div>
      </div>

      {open ? (
        <div className="border-t border-[color:var(--line)] px-4 py-2 md:hidden">
          <label className="control-label mb-2 block px-3" htmlFor="mobile-locale-select">
            {t('nav.language')}
          </label>
          <select
            id="mobile-locale-select"
            className="input-field mb-2 w-full"
            value={locale}
            onChange={(event) => setLocale(event.target.value)}
          >
            {localeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {links.map((href) => (
            <button
              key={href}
              onClick={() => {
                navigate(href);
                setOpen(false);
              }}
              className="block w-full rounded-md px-3 py-3 text-left text-sm font-medium text-[color:var(--text)] hover:bg-[color:var(--panel-strong)]"
            >
              {t(href === '/support' ? 'nav.support' : 'nav.about')}
            </button>
          ))}
        </div>
      ) : null}
    </header>
  );
}
